import React from 'react';
import { Editor } from '@hugerte/hugerte-react';

import './style.scss';

import { Controller, get } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import PropTypes from 'prop-types';

export default function HugerteEditor({
  label,
  name,
  placeholder,
  isRequired,
  disabled,
  readOnly,
  control,
  errors,
  height,
  style,
  defaultValue,
  reset,
}) {
  const { t } = useTranslation();

  // useEffect(() => {
  //   const script = document.createElement('script');
  //   script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-mml-chtml.js';
  //   script.async = true;
  //   document.body.appendChild(script);
  //   return () => {
  //     document.body.removeChild(script);
  //   };
  // }, []);

  return (
    <>
      {label && (
        <label htmlFor={name} className="inline-block mb-2">
          {label}
          {isRequired && (
            <span className="text-red-500" style={{ fontWeight: 900 }}>
              {' '}
              *
            </span>
          )}
        </label>
      )}
      <p className="font-italic text-red-500 mt-0 mb-1 text-sm font-medium">
        {t('formControl.editorInput')} &apos;Times New Roman&apos;
      </p>
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Editor
            value={value}
            onEditorChange={(e) => {
              const editorValue = (e ?? '')?.replace(/<[^>]+>(<br>)+<\/[^>]+>/g, '');
              onChange(editorValue);
            }}
            disabled={readOnly}
            style={{ height: `${height}`, ...style }}
            placeholder={placeholder}
            className={classNames('relative', 'custom-tooltip-for-editor-input', {
              'p-invalid': !!get(errors, name),
              'surface-200': disabled,
              'hide-image-button': true,
              'ql-container': true,
            })}
            init={{
              branding: false,
              plugins: readOnly
                ? 'autoresize'
                : [
                  'accordion',
                  'anchor',
                  'autolink',
                  'autoresize',
                  'codesample',
                  'emoticons',
                  'fullscreen',
                  'insertdatetime',
                  'link',
                  'lists',
                  'preview',
                  'searchreplace',
                  'table',
                  'visualblocks',
                  'wordcount',
                ],
              menubar: readOnly ? false : 'file edit view insert format tools table',
              toolbar: readOnly
                ? false
                : 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough superscript subscript | link table mergetags | addcomment showcomments | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | removeformat',
              extended_valid_elements:
                '+math[*],mrow[*],mo[*],mi[*],mn[*],msup[*],mfrac[*],msqrt[*],mroot[*],mtext[*],mstyle[*],mtable[*],mtr[*],mtd[*],span[*]',
              min_height: readOnly ? 200 : 300,
              statusbar: !readOnly,
              autoresize_bottom_margin: 20,
              forced_root_block_attrs: {
                style: 'font-family: Times New Roman; font-size: 12pt',
              },
              content_style: 'p { margin: 0; }',
              init_instance_callback(editor) {
                if (readOnly && value) {
                  editor.setContent(value);
                }
                if (defaultValue) {
                  editor.setContent(defaultValue);
                  reset({}, { keepValues: true });
                }
              },
              setup: (editor) => {
                const openDialog = (existingLatex = '', selectedNode = null) => {
                  const handleMathOutput = (latexValue) => {
                    const outputP = document.getElementById('math-output');

                    if (outputP && window.MathJax && window.MathJax.tex2mml) {
                      const mathml = window.MathJax.tex2mml(latexValue, { display: false });
                      outputP.innerHTML = `<span contenteditable="false">${mathml}</span>`;
                    }
                  };

                  editor.windowManager.open({
                    title: 'Math equation',
                    size: 'medium',
                    body: {
                      type: 'panel',
                      items: [
                        {
                          type: 'textarea',
                          name: 'latex',
                          label: 'Math Latex',
                          placeholder: '\\frac{a}{b}',
                        },
                        {
                          type: 'htmlpanel',
                          name: 'output',
                          html: `<div style="display: flex; flex-direction: column; margin-top: 8px;">
                                <label for="math-output" >Preview:</label>
                                <p id="math-output" style="margin-top: 8px;"></p>
                              </div>`,
                        },
                      ],
                    },
                    buttons: [
                      {
                        type: 'cancel',
                        text: 'Close',
                      },
                      {
                        type: 'submit',
                        text: 'Save',
                        buttonType: 'primary',
                      },
                    ],
                    initialData: {
                      latex: existingLatex,
                    },
                    onChange: (api) => {
                      const data = api.getData();
                      const latex = data.latex || '';
                      handleMathOutput(latex);
                    },
                    onSubmit: (api) => {
                      const data = api.getData();
                      const latex = data.latex || '';
                      if (window.MathJax && window.MathJax.tex2mml) {
                        const mathmlOutput = window.MathJax.tex2mml(latex, { display: false });
                        const html = `<span fomuala="${latex}" contenteditable="false">
                          ${mathmlOutput}
                        </span>`;

                        if (selectedNode) {
                          // eslint-disable-next-line no-param-reassign
                          selectedNode.outerHTML = html;
                        } else {
                          editor.insertContent(html);
                        }
                      } else {
                        editor.insertContent('<p>Error: MathJax not loaded</p>');
                      }
                      api.close();
                    },
                  });
                  setTimeout(() => {
                    handleMathOutput(existingLatex);
                  }, 0);
                };

                editor.ui.registry.addToggleButton('mathbutton', {
                  icon: 'math-custom-icon',
                  tooltip: 'Insert/edit Math',
                  onAction: () => {
                    const selectedNode = editor.selection.getNode();

                    if (
                      selectedNode &&
                      selectedNode.nodeName === 'SPAN' &&
                      selectedNode.hasAttribute('fomuala')
                    ) {
                      const existingLatex = selectedNode.getAttribute('fomuala') || '';
                      openDialog(existingLatex, selectedNode);
                    } else {
                      openDialog();
                    }
                  },
                  onSetup: (api) => {
                    const selector = 'span[fomuala]';

                    const nodeChangeHandler = (state) => {
                      api.setActive(state);
                    };

                    editor.selection.selectorChanged(selector, nodeChangeHandler);

                    return () => {
                      editor.selection.selectorChanged(selector, nodeChangeHandler);
                    };
                  },
                });
                editor.ui.registry.addIcon(
                  'math-custom-icon',
                  `
                 <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="24" height="24" viewBox="0 0 24 24">
                    <image xlink:href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAv1JREFUSEuF1UmIXFUUBuDvpANmJZKs4gCJG1E3DsHg0A6gJigYCS4lQbdCCOJKWm01G1fizpVCaJBIHFGxUTJookTjUgwiBHEgCoaEKAhCH+u+ul31XtXr7gdF3ffuPdP/n/PfMHoCSf0bf++umu1Vj3Y3y9tkCKMoU8HaH1bKpPt9MsCNyQHsbspZo5qJ5CJ5B8/h++FeNC7qE5tCfpN8gSfWhqhbTcgC8JuYDbYnfw1DjAMskpfjPuLfNh9t3PshHX3dgKPBxQw7G0prHjvxPq4bZP9zl4M1sX4aVwxOzSdL2IIzeBSfFusC1GkZx8lyuP/pjzMjLErbgy0tWF7NcLe0rVRwc3IaV+HckNgSs8F03FHLTA2bueaV65Mf8Q/uEC7Vzc34FduKu+eFHTLuXDaddDtOfrrHMzwkXRZ8lPw3sg1fS58Ui6P191LfRBRugl3D4WpX1kHyj2BzdjN8QbqnmP00gGVeWmhnWiFajwt4Bt92J7ID43ny7HLv1zh7MFd8/l0Z/7wH8tvwFTbhItYNIF2qA7guWBry0SWoJno/3ivrS8LuAQeftSusJqWrHg9uqcSeJA8P2vmtGvhJHOtyNsLhQRwub6ULijwc7OnEd0s3BPtqpo8UowZW/sQDGmJ7e3gvni07R4TjMl6cUNOyd47YF/LQsoIqUhJmpVmcWEWv5onZMgdzycPE7TXAU9KRiu8Z4mryt0rgfvLlQeYnsbXRnHCh9v7kgJ7ChyXLm/AdrsHveL2SvjDkxrVj8mOBfBulIQ4FBzKc6rkgrsQvxK1DqRi24IlgfzKDN7BHOCjtnUK4K6R9N9Brwl0ymkkuIXZIHwyyul44K80EryQf1yHs0afuVLcObMUP2IXFllw3Y70xuXck11Nuu7W0ZbyuN0jHBl13vkrISK6Lq41BuXC+LBdO23ilOD3N2Vw4KAN6vhHqCUc3DKa1aNJjq+LeHvmxg7IqczPXuTJ7R6TvRug52NausQp0BXGyginVnxS4KTlZqdRa2f9FNByduo720AAAAABJRU5ErkJggg==" x="0" y="0" width="24" height="24"/>
                  </svg>
                `
                );
              },
            }}
          />
        )}
      />
      <Tooltip
        target=".custom-tooltip-for-editor-input"
        content={t(get(errors, name)?.message)}
        position="bottom"
      />
    </>
  );
}

HugerteEditor.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  height: PropTypes.string,
  style: PropTypes.shape({}),
  defaultValue: PropTypes.string,
  reset: PropTypes.func,
};

HugerteEditor.defaultProps = {
  label: '',
  placeholder: '',
  isRequired: false,
  disabled: false,
  readOnly: false,
  height: '300px',
  style: {},
  defaultValue: '',
  reset: () => {},
};
